import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, LayoutGrid, Flame } from "lucide-react";
import { StarlightCatcher } from "./games/StarlightCatcher";
import { MemoryMatch } from "./games/MemoryMatch";
import { LanternLauncher } from "./games/LanternLauncher";
import { TiltCard } from "./TiltCard";
import { useLang } from "../store/lang";

const TABS = [
  { id: "starlight", testid: "game-tab-starlight", label: "Stardust Catcher", urLabel: "ستارے پکڑو", Icon: Star },
  { id: "memory", testid: "game-tab-memory", label: "Memory Match", urLabel: "یادداشت کا کھیل", Icon: LayoutGrid },
  { id: "lantern", testid: "game-tab-lantern", label: "Wish Lanterns", urLabel: "دعاؤں کے چراغ", Icon: Flame },
];

export const GamesHub = () => {
  const [tab, setTab] = useState("starlight");
  const { ur } = useLang();

  return (
    <section id="games" className="relative py-28 sm:py-36 px-6" data-testid="games-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="eyebrow mb-4"
          >
            {ur ? "فیری ٹیل کھیلوں کا محل" : "The fairytale kingdom arcade"}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className={`font-display text-3xl sm:text-5xl text-amber-100 font-light ${ur ? "font-urdu" : ""}`}
            dir={ur ? "rtl" : "ltr"}
          >
            {ur ? "چھوٹے اور بڑے مہمانوں کے لیے کھیل" : "Games for little & big guests"}
          </motion.h2>
        </div>

        <div className="flex justify-center gap-2 sm:gap-3 mb-10 flex-wrap">
          {TABS.map(({ id, testid, label, urLabel, Icon }) => (
            <button
              key={id}
              data-testid={testid}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-xs tracking-[0.15em] uppercase transition-colors duration-300 border ${
                tab === id
                  ? "bg-amber-300/15 border-amber-300/60 text-amber-100 shadow-[0_0_20px_rgba(212,175,55,0.25)]"
                  : "border-amber-200/15 text-slate-400 hover:text-amber-200 hover:border-amber-200/40"
              } ${ur ? "font-urdu tracking-normal" : ""}`}
            >
              <Icon size={14} /> {ur ? urLabel : label}
            </button>
          ))}
        </div>

        <TiltCard max={2.5} className="glass-card rounded-3xl p-5 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              {tab === "starlight" && <StarlightCatcher />}
              {tab === "memory" && <MemoryMatch />}
              {tab === "lantern" && <LanternLauncher />}
            </motion.div>
          </AnimatePresence>
        </TiltCard>
      </div>
    </section>
  );
};
